/**
 * Monaco Editor 实时语法检查集成 Judge0 API
 * 提供实时代码检查、错误解析和错误标注功能
 */

import * as monaco from 'monaco-editor';
import { submitCode, getSubmissionResult, LANGUAGE_IDS } from './JudgeService';

// 防抖函数，避免频繁请求
export function debounce(func: Function, wait: number) {
  let timeout: NodeJS.Timeout;
  return function(this: any, ...args: any[]) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

// 使用 JudgeService 中定义的语言 ID
// 不再需要单独定义 API 接口


// 错误严重性映射
export const SEVERITY = {
  ERROR: 8,
  WARNING: 4,
  INFO: 2,
  HINT: 1
};

// 解码base64数据
// 注意：使用增强的 Base64 解码方法处理 UTF-8 字符
// 原生 atob() 只能处理 Latin1 字符，对于中文等非 Latin1 字符会抛出 InvalidCharacterError 错误
export function decodeBase64(str: string): string {
  if (!str) return '';
  
  try {
    // 先使用 atob 将 base64 转为二进制字符串
    const binString = window.atob(str);
    
    // 然后将二进制字符串转为 Uint8Array
    const bytes = new Uint8Array(binString.length);
    for (let i = 0; i < binString.length; i++) {
      bytes[i] = binString.charCodeAt(i);
    }
    
    // 最后使用 TextDecoder 解码为 UTF-8 文本
    return new TextDecoder().decode(bytes);
  } catch (e) {
    console.error('Base64解码失败:', e);
    return '';
  }
}

// 编码base64数据
export function encodeBase64(str: string): string {
  try {
    return btoa(str || '');
  } catch (e) {
    console.error('Base64编码失败:', e);
    return '';
  }
}

// Marker 接口定义
export interface IMarker {
  severity: number;
  startLineNumber: number;
  startColumn: number;
  endLineNumber: number;
  endColumn: number;
  message: string;
}

/**
 * 实时语法检查服务
 */
export class SyntaxChecker {
  private editor: any;
  private monaco: any;
  private currentLanguage: string;
  private markers: IMarker[];
  private lastToken: string | null;
  private isChecking: boolean;
  
  constructor(editor: any, monaco: any) {
    this.editor = editor;
    this.monaco = monaco;
    this.currentLanguage = 'cpp'; // 默认语言
    this.markers = [];
    this.lastToken = null;
    this.isChecking = false;
    
    // 初始化检查器
    this.init();
  }
  
  // 初始化并设置事件监听
  init() {
    // 监听编辑器内容变化
    this.editor.onDidChangeModelContent(debounce(() => {
      this.checkSyntax();
    }, 500));
    
    // 清除所有标记
    this.clearMarkers();
  }
  
  // 设置当前语言
  setLanguage(language: string) {
    this.currentLanguage = language.toLowerCase();
  }
  
  // 清除所有标记
  clearMarkers() {
    this.monaco.editor.setModelMarkers(this.editor.getModel(), 'syntax', []);
    this.markers = [];
  }
  
  // 获取当前语言的 Judge0 语言 ID
  getLanguageId(): number {
    switch (this.currentLanguage) {
      case 'cpp':
        return LANGUAGE_IDS['cpp'];
      case 'python':
        return LANGUAGE_IDS['python'];
      default:
        return LANGUAGE_IDS['cpp']; // 默认使用 C++
    }
  }
  
  // 获取编译器选项
  getCompilerOptions(): string {
    if (this.currentLanguage === 'cpp') {
      return '-std=c++17';
    }
    return '';
  }
  
  // 提交代码到 Judge0
  async submitCode(code: string): Promise<string | null> {
    try {
      const languageId = this.getLanguageId();
      const compilerOptions = this.getCompilerOptions();
      
      // 使用 JudgeService 中的 submitCode 方法
      const data = await submitCode(
        languageId,
        code,
        '', // stdin
        compilerOptions
      );
      
      return data.token;
    } catch (error) {
      console.error('提交代码失败:', error);
      return null;
    }
  }
  
  // 获取提交结果
  async getSubmissionResult(token: string) {
    try {
      // 使用 JudgeService 中的 getSubmissionResult 方法
      const result = await getSubmissionResult(token);
      return result;
    } catch (error) {
      console.error('获取提交结果失败:', error);
      return null;
    }
  }
  
  // 解析 C++ 编译错误
  parseCppErrors(errorText: string): IMarker[] {
    const markers: IMarker[] = [];
    const lines = errorText.split('\n');
    
    for (const line of lines) {
      // 匹配常见的 C++ 错误格式: <file>:<line>:<column>: error: <message>
      const match = line.match(/.*:(\d+):(\d+):\s+(error|warning|note):\s+(.*)/);
      if (match) {
        const lineNumber = parseInt(match[1], 10);
        const column = parseInt(match[2], 10);
        const type = match[3]; // error, warning, note
        const message = match[4];
        
        markers.push({
          severity: type === 'error' ? SEVERITY.ERROR : 
                    type === 'warning' ? SEVERITY.WARNING : SEVERITY.INFO,
          startLineNumber: lineNumber,
          startColumn: column,
          endLineNumber: lineNumber,
          // endColumn: column + 1, // 原来的逻辑，只覆盖一个字符
          // 修改开始
          endColumn: (() => {
            const model = this.editor.getModel();
            if (!model) return column + 1; // 安全检查
            const lineContent = model.getLineContent(lineNumber);
            const wordAtPosition = model.getWordAtPosition({ lineNumber: lineNumber, column: column });
            if (wordAtPosition) {
              return wordAtPosition.endColumn;
            }
            // 如果无法获取词语，或者错误不在词语上，则从错误列覆盖到行尾
            return lineContent.length + 1;
          })(),
          message: message
        });
      }
    }
    
    return markers;
  }
  
  // 解析 Python 错误
  parsePythonErrors(errorText: string): IMarker[] {
    const markers: IMarker[] = [];
    const lines = errorText.split('\n');
    const model = this.editor.getModel();
    if (!model) return markers;

    for (let i = 0; i < lines.length; i++) {
      const lineText = lines[i];
      // Match "File \"...\", line X" to get the line number
      const fileLineMatch = lineText.match(/File ".*?", line (\d+)/);
      if (fileLineMatch) {
        const errorLineNumber = parseInt(fileLineMatch[1], 10);
        if (isNaN(errorLineNumber) || errorLineNumber <= 0 || errorLineNumber > model.getLineCount()) continue;

        const actualCodeLineIndex = lines.findIndex((l, idx) => idx > i && l.trim().length > 0 && !l.startsWith(' ') && !l.startsWith('^'));
        const errorMessageLineIndex = lines.findIndex((l, idx) => idx > i && (l.includes('Error:') || l.includes('Exception:')));
        
        let errorMessage = '';
        if (errorMessageLineIndex !== -1 && errorMessageLineIndex < lines.length) {
            errorMessage = lines[errorMessageLineIndex].trim();
        } else if (i + 1 < lines.length && lines[i+1].trim()) {
            // Fallback if specific Error/Exception line not found
            errorMessage = lines[i+1].trim();
        }
        if (!errorMessage) continue; // No error message found for this line info

        const lineContent = model.getLineContent(errorLineNumber);
        let startColumn = 1;
        let endColumn = lineContent.length + 1; // Default to full line

        // Attempt to find '^' indicator for SyntaxError
        const caretLineIndex = lines.findIndex((l, idx) => idx > i && l.trim().startsWith('^'));
        if (errorMessage.includes('SyntaxError') && caretLineIndex !== -1 && caretLineIndex < lines.length) {
          const caretPosition = lines[caretLineIndex].indexOf('^');
          if (caretPosition !== -1) {
            startColumn = caretPosition + 1;
            const wordAtCaret = model.getWordAtPosition({ lineNumber: errorLineNumber, column: startColumn });
            if (wordAtCaret) {
              startColumn = wordAtCaret.startColumn;
              endColumn = wordAtCaret.endColumn;
            } else {
              endColumn = lineContent.length + 1; // Fallback to end of line if no word at caret
            }
          }
        } else {
          // For other errors (NameError, TypeError, etc.) or SyntaxError without '^'
          // Try to extract keyword from error message (e.g., name 'xxx' is not defined)
          const keywordMatch = errorMessage.match(/'([^']+)'/);
          if (keywordMatch && keywordMatch[1]) {
            const keyword = keywordMatch[1];
            const keywordPos = lineContent.indexOf(keyword);
            if (keywordPos !== -1) {
              startColumn = keywordPos + 1;
              endColumn = keywordPos + 1 + keyword.length;
            }
          } else if (errorMessage.includes('SyntaxError')) {
            // For SyntaxErrors that might not have a clear '^' or quoted keyword
            // e.g. "invalid character '§' (U+00A7)"
            const invalidCharMatch = errorMessage.match(/invalid character '(.+)'/i) || errorMessage.match(/unexpected EOF/i) || errorMessage.match(/invalid syntax/i) ;
            if (invalidCharMatch && invalidCharMatch[1]) {
                const char = invalidCharMatch[1];
                const charPos = lineContent.indexOf(char);
                if (charPos !== -1) {
                    startColumn = charPos +1;
                    endColumn = charPos + 1 + char.length;
                }
            } else if (invalidCharMatch) { // For general 'invalid syntax' or 'unexpected EOF'
                 startColumn = 1;
                 endColumn = lineContent.length + 1;
            }
          }
        }

        markers.push({
          severity: SEVERITY.ERROR,
          startLineNumber: errorLineNumber,
          startColumn: startColumn,
          endLineNumber: errorLineNumber,
          endColumn: endColumn,
          message: errorMessage
        });
        // Skip lines already processed as part of this error block
        if (caretLineIndex !== -1) i = caretLineIndex;
        else if (actualCodeLineIndex !== -1 && actualCodeLineIndex > i) i = actualCodeLineIndex;
        else if (errorMessageLineIndex !== -1 && errorMessageLineIndex > i) i = errorMessageLineIndex;
      }
    }
    return markers;
  }
  
  // 解析错误并创建标记
  parseErrors(result: any): IMarker[] {
    let markers: IMarker[] = [];

    // 解析编译错误 (compile_output)
    // JudgeService 返回的 result.compile_output 已经是解码后的字符串，无需再次解码。
    if (result && result.compile_output && typeof result.compile_output === 'string') {
      const compileOutput = result.compile_output.trim();
      if (compileOutput) {
        if (this.currentLanguage === 'cpp') {
          markers = markers.concat(this.parseCppErrors(compileOutput));
        }
        // 可以为其他语言添加 compile_output 的处理逻辑
        // else if (this.currentLanguage === 'java') { ... }
      }
    }

    // 解析标准错误输出 (stderr) - 主要用于 Python 错误，但也可能包含其他语言的运行时错误
    // JudgeService 返回的 result.stderr 已经是解码后的字符串，无需再次解码。
    if (result && result.stderr && typeof result.stderr === 'string') {
      const stderr = result.stderr.trim();
      if (stderr) {
        if (this.currentLanguage === 'python') {
          markers = markers.concat(this.parsePythonErrors(stderr));
        } else {
          // 对于非 Python 语言，如果 stderr 有内容，也应作为错误标记显示
          // 这有助于捕获 C++ 的运行时错误或其他语言的通用错误信息
          markers.push({
            severity: SEVERITY.ERROR,
            startLineNumber: 1, // 默认标记在第一行，可能需要更智能的行号提取
            startColumn: 1,
            endLineNumber: 1,
            endColumn: 1000, // 标记整行
            message: stderr
          });
        }
      }
    }

    return markers;
  }
  
  // 设置错误标记
  setMarkers(markers: IMarker[]) {
    this.markers = markers;
    this.monaco.editor.setModelMarkers(this.editor.getModel(), 'syntax', markers);
  }
  
  // 执行语法检查
  async checkSyntax() {
    // 避免并发检查
    if (this.isChecking) return;
    
    try {
      this.isChecking = true;
      
      // 获取当前代码
      const code = this.editor.getValue();
      if (!code.trim()) {
        this.clearMarkers();
        return;
      }
      
      // 提交代码
      const token = await this.submitCode(code);
      if (!token) {
        console.error('未能获取提交令牌');
        return;
      }
      
      this.lastToken = token;
      
      // 等待结果 (最多尝试5次，每次间隔1秒)
      let result = null;
      let attempts = 0;
      
      while (attempts < 5) {
        const data = await this.getSubmissionResult(token);
        
        // 如果状态不是"处理中"，则获取结果
        if (data && data.status && data.status.id !== 1 && data.status.id !== 2) {
          result = data;
          break;
        }
        
        // 等待1秒后再次尝试
        await new Promise(resolve => setTimeout(resolve, 1000));
        attempts++;
      }
      
      // 解析错误并设置标记
      if (result) {
        const markers = this.parseErrors(result);
        this.setMarkers(markers);
      }
    } catch (error) {
      console.error('语法检查失败:', error);
    } finally {
      this.isChecking = false;
    }
  }
}

/**
 * 初始化语法检查器
 * @param {Object} editor - Monaco Editor 实例
 * @param {Object} monaco - Monaco 全局对象
 * @returns {SyntaxChecker} 语法检查器实例
 */
export function initSyntaxChecker(editor: any, monaco: any): SyntaxChecker {
  return new SyntaxChecker(editor, monaco);
}
